import { absoluteValue } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import { AssetNotFoundException } from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { sodexAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { normalizeUserAddress } from '../normalizeUserAddress'
import { resolveCoinAsset } from '../resolveCoinAsset'
import { responseSchema } from './schema'

/**
 * 先物ファンディング履歴 API から Action 一覧を取得する
 */
export const fetchApiData: FetchApiData = async ({
  apiKey,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  const getAsset = createGetAsset({ assets, symbolAssetMap })
  const userAddress = normalizeUserAddress({ address: apiKey })

  const client = createClient({
    market: 'perps',
    createHeaders,
    proxy,
  })

  const query: Record<string, string> = {
    limit: config.limit.toString(),
    ...(from ? { startTime: from.toString() } : {}),
    ...(to ? { endTime: to.toString() } : {}),
  }

  const fundings =
    (await client.run({
      method: 'GET',
      path: `/accounts/${userAddress}/fundings`,
      query,
      responseSchema,
    })) ?? []

  const sortedFundings = fundings.toSorted((a, b) => a.timestamp - b.timestamp)

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const funding of sortedFundings) {
    fetchedFrom =
      fetchedFrom === undefined
        ? funding.timestamp
        : Math.min(fetchedFrom, funding.timestamp)
    fetchedTo =
      fetchedTo === undefined
        ? funding.timestamp
        : Math.max(fetchedTo, funding.timestamp)

    if (funding.fundingFee.value === 0n) {
      continue
    }

    const asset = resolveCoinAsset({ coin: funding.feeCoin, getAsset })
    if (!asset) {
      throw new AssetNotFoundException(funding.feeCoin)
    }

    // fundingFee: negative = received, positive = paid
    const amount = funding.fundingFee

    actions.push({
      comment: `Funding of ${funding.symbol}/${funding.feeCoin}`,
      evidence: 'system-rule',
      order: 0,
      source: `${sodexAccountCategory}_${config.type}_${funding.positionID}_${funding.timestamp}`,
      type: amount.value > 0n ? 'derivative-fee' : 'derivative-profit',
      timestamp: funding.timestamp,
      transfers: [
        {
          asset,
          amount: absoluteValue(amount),
          direction: amount.value > 0n ? 'out' : 'in',
        },
      ],
    })
  }

  const sortedActions = actions.toSorted((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: fundings.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
